/**
 * Экран «Интеграции»: Битрикс24, робот обзвона, правила приёма номеров.
 *
 * Раньше и портал, и робот настраивались переменными окружения — то есть
 * менялись редеплоем, а админ не мог сделать этого вовсе. Всё переехало
 * в таблицу `settings`: адрес портала, воронка, ответственный, адрес и ключ
 * робота, круг выгрузки, расписание (§9.3).
 *
 * Секреты (адрес вебхука портала несёт в себе токен, ключ робота — сам секрет)
 * хранятся в той же таблице, но наружу НИКОГДА не отдаются целиком: в ответе
 * лежит маска с последними 4 символами. Отправить маску обратно нельзя —
 * сервер отвергает значение с символом маски, иначе «сохранил не трогая»
 * записал бы в базу строку из точек и тихо оборвал интеграцию.
 */
import { z } from 'zod';
import { isoDateTimeSchema } from './common.dto.js';
import { settingValueSchema } from './settings.dto.js';
/** Символ маски секрета. Один на весь проект: по нему же сервер ловит возврат маски */
export const SECRET_MASK_CHAR = '•';
/** Все поля экрана «Интеграции» — и собственные, и заимствованные из общих настроек */
export const integrationFieldKeySchema = z.enum([
    // Битрикс24
    'bitrix.enabled',
    'bitrix.webhook_url',
    'bitrix.pipeline_id',
    'bitrix.assigned_by_id',
    'bitrix.send_contact',
    'bitrix.send_lead',
    'bitrix.send_deal',
    // Телеграм: канал, куда падают лиды с комментарием диспетчера
    'telegram.enabled',
    'telegram.bot_token',
    'telegram.chat_id',
    'telegram.send_lead',
    // Робот исходящего обзвона
    'robot.enabled',
    'robot.api_url',
    'robot.api_key',
    'robot.push_from_round',
    'robot.export_times',
    'robot.on_fail',
    // Приём номеров: общие правила дедупликации
    'dedup.requeue_after_days',
    'dedup.lead_lock_days',
    'attribution.window_days',
]);
/**
 * Ключи-секреты: наружу уходят маской, в историю настроек тоже пишутся маской.
 * Плановая копия секрета в `settings_history` не защищена ротацией и живёт вечно.
 */
export const INTEGRATION_SECRET_KEYS = [
    'bitrix.webhook_url',
    'robot.api_key',
    // Токен бота — сам по себе полный доступ к боту: кто его увидел, тот пишет
    // в канал от нашего имени и читает всё, что боту доступно
    'telegram.bot_token',
];
/**
 * Ключи, которые экран делит с общими настройками. Они правятся тем же
 * механизмом, что и на экране «Настройки»: версионирование в settings_history
 * и, для окна атрибуции, пересчёт витрин. Второго пути записи у них нет.
 */
export const INTEGRATION_SHARED_KEYS = [
    'robot.enabled',
    'robot.on_fail',
    'dedup.requeue_after_days',
    'dedup.lead_lock_days',
    'attribution.window_days',
];
export const integrationFieldSchema = z.object({
    key: integrationFieldKeySchema,
    /** У секрета здесь маска, а не значение */
    value: settingValueSchema,
    isSecret: z.boolean(),
    /** Секрет задан. «Пусто» и «скрыто» — разные состояния, и путать их нельзя */
    isSet: z.boolean(),
    updatedAt: isoDateTimeSchema.nullable(),
    updatedByName: z.string().nullable(),
});
export const integrationsResponseSchema = z.object({
    items: z.array(integrationFieldSchema),
    /**
     * Чего не хватает, чтобы включённая интеграция реально работала.
     * Считает сервер: только он видит настоящие значения секретов.
     */
    warnings: z.array(z.string()),
});
/** Путь /:key — ключ обязан быть из закрытого списка, иначе 400 ещё до домена */
export const integrationKeyParamSchema = z.object({ key: integrationFieldKeySchema });
/** PUT /api/integrations/:key — по одному ключу за раз, как и общие настройки */
export const updateIntegrationBodySchema = z.object({
    value: settingValueSchema,
    note: z.string().max(300).optional(),
});
export const updateIntegrationResponseSchema = z.object({
    key: integrationFieldKeySchema,
    /** Снова маска, если ключ секретный: ответ на запись тоже не показывает секрет */
    value: settingValueSchema,
    isSet: z.boolean(),
    /**
     * Правка заимствованной настройки может переписать трактовку уже собранных
     * цифр — тогда пересчёт витрин за затронутый период ставится в очередь (§7.5)
     */
    recalcScheduled: z.boolean(),
});
/** Элемент справочника портала: воронка или сотрудник */
export const bitrixOptionSchema = z.object({ id: z.string(), title: z.string() });
/**
 * POST /api/integrations/bitrix/check — «Проверить».
 *
 * Проверка идёт по СОХРАНЁННОМУ адресу: показывать зелёную галочку на значении
 * из поля ввода, которое ещё не в базе, значит врать — лиды поедут по другому.
 */
export const bitrixCheckResponseSchema = z.object({
    ok: z.boolean(),
    /** Что показать человеку: имя портала или причина отказа */
    message: z.string(),
    portal: z
        .object({
        name: z.string().nullable(),
        userName: z.string().nullable(),
        userId: z.string().nullable(),
        timeZone: z.string().nullable(),
    })
        .nullable(),
    /** Воронки сделок портала: из них выбирается воронка отправки */
    pipelines: z.array(bitrixOptionSchema),
    /** Активные сотрудники портала: из них выбирается ответственный */
    responsibles: z.array(bitrixOptionSchema),
    /** Сырой ответ портала строкой — его показывают поддержке как есть */
    raw: z.string().nullable(),
});
/**
 * Проверка телеграма шлёт в канал настоящее сообщение.
 *
 * Иначе проверять нечего: `getMe` подтверждает только токен, а типовые
 * отказы — «бот не в канале» и «нет права публиковать» — вылезают ровно на
 * отправке, то есть на первом живом лиде.
 */
export const telegramCheckResponseSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
});
//# sourceMappingURL=integrations.dto.js.map